import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search,
  RefreshCw,
  Download,
  CheckCircle,
  XCircle,
  AlertCircle,
  MinusCircle,
  ChevronDown,
  ChevronRight,
  Eye,
  TrendingUp,
  Users,
  BookOpen,
  Calendar,
  ArrowUpDown,
  PieChart,
  SlidersHorizontal,
  X,
  Loader2,
  Zap
} from 'lucide-react';
import api from '../../api';

const AggregatedMarksView = ({ 
  classItem, 
  subject, 
  facultyId, 
  tenantId,
  isActive,
  programTypeId,
  programId,
  academicYearId,
  termId,
  semesterId,
  batchId,        
  batchNumber
}) => {
  const [loading, setLoading] = useState(false);
  const [aggregatedData, setAggregatedData] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [eligibilityFilter, setEligibilityFilter] = useState('all');
  const [sortColumn, setSortColumn] = useState('studentName');
  const [, setSortDirection] = useState('asc');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // State for manual aggregation
  const [recalculating, setRecalculating] = useState(false);
  const [recalcTarget, setRecalcTarget] = useState(null);
  const [recalcProgress, setRecalcProgress] = useState(null);

  const fetchAggregatedMarks = React.useCallback(async () => {
    if (!classItem || !subject || !facultyId) {
      setError('Missing required data');
      return;
    }

    const classId = classItem?.classId || classItem?._id;
    const subjectId = subject?.subjectId || subject?._id;
    const finalProgramTypeId = programTypeId || classItem?.programTypeId;
    const finalProgramId = programId || classItem?.programId;
    const finalAcademicYearId = academicYearId || classItem?.academicYear?._id || classItem?.academicYearId;
    const finalTermId = termId || classItem?.termInfo?._id || classItem?.termId;
    const finalSemesterId = semesterId || classItem?.semesterInfo?._id || classItem?.semesterId;
    const finalTenantId = tenantId || classItem?.tenantId || localStorage.getItem('tenantId');

    const missingParams = [];
    if (!classId) missingParams.push('classId');
    if (!subjectId) missingParams.push('subjectId');
    if (!finalProgramTypeId) missingParams.push('programTypeId');
    if (!finalProgramId) missingParams.push('programId');
    if (!finalAcademicYearId) missingParams.push('academicYearId');
    if (!finalTermId && !finalSemesterId) missingParams.push('termId or semesterId');

    if (missingParams.length > 0) {
      setError(`Missing: ${missingParams.join(', ')}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        classId: classId.toString(),
        subjectId: subjectId.toString(),
        programTypeId: finalProgramTypeId.toString(),
        programId: finalProgramId.toString(),
        academicYearId: finalAcademicYearId.toString(),
        facultyId: facultyId.toString()
      });

      if (finalTenantId) {
        params.append('tenantId', finalTenantId.toString());
      }

      if (finalSemesterId) {
        params.append('semesterId', finalSemesterId.toString());
      } else if (finalTermId) {
        params.append('termId', finalTermId.toString());
      }

      if (batchId) {
        params.append('batchId', batchId);
      }
      if (batchNumber) {
        params.append('batchNumber', batchNumber);
      }

      const response = await api.get(`/api/faculty/aggregated-marks-view?${params}`, {
        headers: {
          'x-tenant-id': finalTenantId.toString()
        }
      });

      if (response.data.success) {
        setAggregatedData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching aggregated marks:', err);
      setError(err.response?.data?.message || err.message || 'Network error');
    } finally {
      setLoading(false);
    }
    }, [classItem, subject, facultyId, programTypeId, programId, academicYearId, termId, semesterId, tenantId, batchId, batchNumber]);
  useEffect(() => {
    if (isActive) {
      fetchAggregatedMarks();
    }
  }, [isActive, fetchAggregatedMarks]);

  // ========== MANUAL AGGREGATION TRIGGER ==========
  
  /**
   * Trigger manual CIE aggregation recalculation
   * This is useful when attendance is updated or marks are corrected
   */
  const triggerManualAggregation = async (type = 'all', studentId = null) => {
    setRecalculating(true);
    setRecalcProgress(null);
    
    const classId = classItem?.classId || classItem?._id;
    const subjectId = subject?.subjectId || subject?._id;
    
    if (!classId || !subjectId) {
      setError('Cannot trigger aggregation: Missing class or subject information');
      setRecalculating(false);
      return;
    }
    
    // Show toast/notification
    const toast = document.createElement('div');
    toast.className = 'fixed bottom-4 right-4 z-50 bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm animate-pulse';
    toast.innerText = '🔄 Triggering CIE aggregation recalculation...';
    document.body.appendChild(toast);
    
    try {
      const payload = {
        classId: classId.toString(),
        subjectId: subjectId.toString()
      };
      
      if (studentId) {
        payload.studentId = studentId;
        setRecalcTarget('student');
      } else if (type === 'component') {
        // For component-specific recalculation, we'd need componentId
        // This would come from the template structure
        const template = aggregatedData?.template;
        if (template && template.components && template.components.length > 0) {
          // Use the first component as default, or you could add UI to select
          payload.componentId = template.components[0]?.id;
          setRecalcTarget('component');
        } else {
          payload.recalculateAll = true;
          setRecalcTarget('all');
        }
      } else {
        payload.recalculateAll = true;
        setRecalcTarget('all');
      }
      
      const response = await api.post('/api/faculty/marks-entry/recalculate-aggregation', payload);
      
      toast.innerText = response.data.success ? '✅ CIE aggregation recalculated successfully!' : '⚠️ ' + response.data.message;
      toast.className = response.data.success 
        ? 'fixed bottom-4 right-4 z-50 bg-emerald-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm'
        : 'fixed bottom-4 right-4 z-50 bg-amber-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm';
      
      if (response.data.success) {
        setRecalcProgress(response.data.data);
        // Refresh the aggregated data to show updated values
        setTimeout(() => {
          fetchAggregatedMarks();
        }, 1000);
      }
      
      setTimeout(() => {
        toast.remove();
      }, 3000);
      
    } catch (err) {
      console.error('Manual aggregation error:', err);
      toast.innerText = '❌ Failed to recalculate aggregation: ' + (err.response?.data?.message || err.message);
      toast.className = 'fixed bottom-4 right-4 z-50 bg-rose-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm';
      setTimeout(() => {
        toast.remove();
      }, 4000);
    } finally {
      setRecalculating(false);
      setRecalcTarget(null);
      setTimeout(() => setRecalcProgress(null), 5000);
    }
  };
  
  /**
   * Trigger aggregation for a specific student from the modal
   */
  const triggerStudentAggregation = async (student) => {
    if (!student || !student.studentId) return;
    setShowDetailsModal(false);
    await triggerManualAggregation('student', student.studentId);
  };

  const getComponentMaxContribution = React.useCallback((weightage) => {
    const cieMaxMarks = aggregatedData?.summary?.cieMaxMarks || 40;
    return (weightage / 100) * cieMaxMarks;
  }, [aggregatedData?.summary?.cieMaxMarks]);

  const getAggregationRuleDisplay = (rule, nValue) => {
    if (!rule) return null;
    if (rule === 'sum_of_all') return 'Sum of All';
    if (rule === 'average_of_all') return 'Average of All';
    if (rule === 'best_of_n') return `Best ${nValue || 1}`;
    if (rule === 'first_n') return `First ${nValue || 1}`;
    if (rule === 'last_n') return `Last ${nValue || 1}`;
    if (rule?.startsWith('best_of_')) {
      const n = rule.split('_')[2];
      return `Best ${n}`;
    }
    if (rule?.startsWith('first_')) {
      const n = rule.split('_')[1];
      return `First ${n}`;
    }
    if (rule?.startsWith('last_')) {
      const n = rule.split('_')[1];
      return `Last ${n}`;
    }
    return rule;
  };

  const getRuleDescription = (rule, nValue) => {
    if (!rule) return null;
    if (rule === 'sum_of_all') return 'Sums ALL assessments';
    if (rule === 'average_of_all') return 'Averages ALL assessments';
    if (rule === 'best_of_n') return `Takes the BEST ${nValue || 1} assessment${(nValue || 1) > 1 ? 's' : ''}`;
    if (rule === 'first_n') return `Takes the FIRST ${nValue || 1} assessment${(nValue || 1) > 1 ? 's' : ''}`;
    if (rule === 'last_n') return `Takes the LAST ${nValue || 1} assessment${(nValue || 1) > 1 ? 's' : ''}`;
    if (rule?.startsWith('best_of_')) {
      const n = rule.split('_')[2];
      return `Takes the BEST ${n} assessment${n > 1 ? 's' : ''}`;
    }
    if (rule?.startsWith('first_')) {
      const n = rule.split('_')[1];
      return `Takes the FIRST ${n} assessment${n > 1 ? 's' : ''}`;
    }
    if (rule?.startsWith('last_')) {
      const n = rule.split('_')[1];
      return `Takes the LAST ${n} assessment${n > 1 ? 's' : ''}`;
    }
    return null;
  };

  const displayColumns = useMemo(() => {
    if (!aggregatedData?.template?.components) return [];
    const columns = [];
    aggregatedData.template.components.forEach(comp => {
      if (comp.type === 'parent' && comp.children && comp.children.length > 0) {
        columns.push({
          id: comp.id,
          name: comp.name,
          type: 'parent',
          weight: comp.weightageInCIE,
          maxContribution: getComponentMaxContribution(comp.weightageInCIE),
          aggregationRule: comp.aggregationRule,
          nValue: comp.nValue,
          children: comp.children
        });
      } else if (comp.type === 'attendance') {
        columns.push({
          id: comp.id,
          name: comp.name,
          type: 'attendance',
          weight: comp.weightageInCIE,
          maxContribution: getComponentMaxContribution(comp.weightageInCIE)
        });
      } else if (comp.type === 'single') {
        columns.push({
          id: comp.id,
          name: comp.name,
          type: 'single',
          weight: comp.weightageInCIE,
          maxContribution: getComponentMaxContribution(comp.weightageInCIE),
          referenceMaxMarks: comp.referenceMaxMarks
        });
      }
    });
    return columns;
  }, [aggregatedData, getComponentMaxContribution]);

  const getComponentDisplay = (student, column) => {
    if (!student.hasAggregation || !student.components) {
      return {
        displayValue: '—',
        earnedScore: 0,
        maxScore: column.maxContribution,
        percentage: 0,
        hasData: false,
        isEligible: false,
        children: [],
        aggregationRule: null,
        nValue: null
      };
    }
    
    if (column.type === 'parent') {
      const parentComp = student.components.find(c => 
        c.type === 'parent' && c.id === column.id
      );
      if (parentComp) {
        const earnedScore = parentComp.contributionToCIE || 0;
        const maxScore = column.maxContribution;
        const percentage = maxScore > 0 ? (earnedScore / maxScore) * 100 : 0;
        const isEligible = percentage >= (student.academicEligibility?.required || 40);
        
        const childrenWithData = (column.children || []).map(child => {
          const childData = parentComp.children?.find(c => c.id === child.id);
          return {
            id: child.id,
            name: child.name,
            rawScore: childData?.rawScore || 0,
            maxRawScore: child.referenceMaxMarks || 100,
            contributionToCIE: childData?.contributionToCIE || 0,
            isSelected: childData?.isSelected === true
          };
        });
        
        return {
          displayValue: `${earnedScore.toFixed(2)}/${maxScore.toFixed(0)}`,
          earnedScore: earnedScore,
          maxScore: maxScore,
          percentage: percentage,
          hasData: true,
          isEligible: isEligible,
          aggregationRule: parentComp.aggregationRuleApplied || column.aggregationRule,
          nValue: parentComp.nValue || column.nValue,
          children: childrenWithData
        };
      }
    }
    else if (column.type === 'attendance') {
      const attendanceComp = student.components.find(c => c.type === 'attendance');
      if (attendanceComp) {
        const earnedScore = attendanceComp.contributionToCIE || 0;
        const maxScore = column.maxContribution;
        const percentage = maxScore > 0 ? (earnedScore / maxScore) * 100 : 0;
        const isEligible = attendanceComp.isEligible || false;
        return {
          displayValue: `${earnedScore.toFixed(2)}/${maxScore.toFixed(0)}`,
          earnedScore: earnedScore,
          maxScore: maxScore,
          percentage: percentage,
          hasData: true,
          isEligible: isEligible,
          children: [],
          aggregationRule: null,
          nValue: null
        };
      }
    }
    else if (column.type === 'single') {
      const singleComp = student.components.find(c => c.type === 'single' && c.id === column.id);
      if (singleComp && singleComp.contributionToCIE > 0) {
        const earnedScore = singleComp.contributionToCIE || 0;
        const maxScore = column.maxContribution;
        const percentage = maxScore > 0 ? (earnedScore / maxScore) * 100 : 0;
        const isEligible = percentage >= (student.academicEligibility?.required || 40);
        return {
          displayValue: `${earnedScore.toFixed(2)}/${maxScore.toFixed(0)}`,
          earnedScore: earnedScore,
          maxScore: maxScore,
          percentage: percentage,
          hasData: true,
          isEligible: isEligible,
          children: [],
          aggregationRule: null,
          nValue: null
        };
      }
    }
    
    return {
      displayValue: '—',
      earnedScore: 0,
      maxScore: column.maxContribution,
      percentage: 0,
      hasData: false,
      isEligible: false,
      children: [],
      aggregationRule: null,
      nValue: null
    };
  };

  const toggleRowExpand = (studentId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(studentId)) {
      newExpanded.delete(studentId);
    } else {
      newExpanded.add(studentId);
    }
    setExpandedRows(newExpanded);
  };

  const viewStudentDetails = (student) => {
    setSelectedStudent(student);
    setShowDetailsModal(true);
  };

  const sortedStudents = useMemo(() => {
    if (!aggregatedData?.students) return [];
    let filtered = [...aggregatedData.students];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(student => 
        student.studentName?.toLowerCase().includes(term) ||
        student.studentUSN?.toLowerCase().includes(term)
      );
    }
    if (eligibilityFilter !== 'all') {
      filtered = filtered.filter(student => 
        eligibilityFilter === 'eligible' ? student.isEligible : !student.isEligible
      );
    }
    // Always sort by USN ascending
    filtered.sort((a, b) => {
      const usnA = (a.studentUSN || '').toLowerCase();
      const usnB = (b.studentUSN || '').toLowerCase();
      if (usnA < usnB) return -1;
      if (usnA > usnB) return 1;
      return 0;
    });
    return filtered;
  }, [aggregatedData, searchTerm, eligibilityFilter]);

  const handleSort = (columnName) => {
    if (sortColumn === columnName) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnName);
      setSortDirection('asc');
    }
  };

  const exportToCSV = () => {
    if (!sortedStudents.length) return;
    
    const headers = ['USN', 'Student Name', 'Status'];
    
    displayColumns.forEach(col => {
      headers.push(`${col.name} (Earned/Max)`);
      headers.push(`${col.name} (%)`);
    });
    
    headers.push('Total CIE Score', 'CIE Percentage', 'Academic Eligibility', 'Attendance Eligibility');
    
    const rows = sortedStudents.map(student => {
      const row = [student.studentUSN, student.studentName, student.isEligible ? 'Eligible' : 'Not Eligible'];
      
      displayColumns.forEach(col => {
        const { displayValue, percentage, hasData } = getComponentDisplay(student, col);
        row.push(hasData ? displayValue : '—');
        row.push(hasData ? `${percentage.toFixed(1)}%` : '—');
      });
      
      row.push(student.totalCIEScore?.toFixed(2) || '0');
      row.push(`${student.totalCIEPercentage?.toFixed(1) || 0}%`);
      row.push(student.academicEligibility?.isEligible ? 'Eligible' : 'Not Eligible');
      row.push(student.attendanceEligibility?.isEligible ? 'Eligible' : 'Not Eligible');
      
      return row;
    });
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aggregated_marks_${aggregatedData?.subject?.code || 'subject'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const StudentDetailsModal = () => {
    if (!selectedStudent) return null;
    
    return (
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 animate-fade-in">
        <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] sm:max-h-[85vh] overflow-hidden flex flex-col transform transition-all animate-slide-up">
          <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/70">
            <div>
              <h3 className="text-base font-semibold text-slate-800">{selectedStudent.studentName}</h3>
              <p className="text-xs font-mono text-slate-500 mt-0.5">{selectedStudent.studentUSN}</p>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => triggerStudentAggregation(selectedStudent)}
                disabled={recalculating}
                className="p-1.5 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-medium"
                title="Recalculate CIE Aggregation for this student"
              >
                {recalculating && recalcTarget === 'student' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Recalc</span>
              </button>
              <button 
                onClick={() => setShowDetailsModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="p-5 overflow-y-auto space-y-5 flex-1 bg-white">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className={`p-4 rounded-xl border ${selectedStudent.isEligible ? 'bg-emerald-50/50 border-emerald-100 text-emerald-800' : 'bg-rose-50/50 border-rose-100 text-rose-800'}`}>
                <span className="text-xs font-medium uppercase tracking-wider opacity-70">Overall Academic Status</span>
                <div className="text-xl font-bold mt-1 flex items-center gap-1.5">
                  {selectedStudent.isEligible ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                      Eligible
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 text-rose-500 shrink-0" />
                      Not Eligible
                    </>
                  )}
                </div>
                <div className="text-xs opacity-80 mt-1.5 font-medium">{selectedStudent.eligibilityReason}</div>
              </div>
              
              <div className="p-4 rounded-xl border border-indigo-100 bg-indigo-50/40 text-indigo-900">
                <span className="text-xs font-medium uppercase tracking-wider opacity-70">Calculated CIE Progress</span>
                <div className="text-xl font-bold mt-1 text-indigo-700 font-mono">
                  {selectedStudent.totalCIEScore?.toFixed(2)}<span className="text-sm font-normal text-slate-400"> / {selectedStudent.totalCIEMaxMarks}</span>
                </div>
                <div className="text-xs text-indigo-600 mt-1.5 font-medium">
                  {selectedStudent.totalCIEPercentage?.toFixed(1)}% Score <span className="text-slate-400 font-normal">(Req: {selectedStudent.academicEligibility?.required}%)</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-3.5">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Component Allocations</h4>
              {selectedStudent.components?.map(comp => {
                const column = displayColumns.find(c => c.id === comp.id);
                const aggregationRule = column?.aggregationRule || comp.aggregationRuleApplied;
                const nValue = column?.nValue || comp.nValue;
                const maxContribution = column?.maxContribution || getComponentMaxContribution(comp.weightageInCIE);
                const earnedScore = comp.contributionToCIE || 0;
                const ruleDisplay = getAggregationRuleDisplay(aggregationRule, nValue);
                const ruleDescription = getRuleDescription(aggregationRule, nValue);
                
                return (
                  <div key={comp.id} className="border border-slate-150 rounded-xl overflow-hidden bg-white shadow-sm">
                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800 text-sm">{comp.name}</span>
                        {ruleDisplay && (
                          <span className="text-[10px] font-medium bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full border border-indigo-100">
                            {ruleDisplay}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-4 text-xs font-medium text-slate-500">
                        <span>Weight: {comp.weightageInCIE}%</span>
                        <span className="text-indigo-600 font-mono">
                          Score: {earnedScore.toFixed(2)}/{maxContribution.toFixed(0)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-3 sm:p-4">
                      {comp.type === 'parent' && comp.children && comp.children.length > 0 && (
                        <div className="overflow-x-auto">
                          {ruleDescription && (
                            <div className="text-xs text-slate-400 mb-2.5 bg-slate-50 p-2 rounded-lg border border-dashed">
                              💡 {ruleDescription}
                            </div>
                          )}
                          
                          <table className="min-w-full text-xs">
                            <thead>
                              <tr className="border-b border-slate-100 text-slate-400 font-semibold">
                                <th className="text-left pb-2 font-medium">Assessment Item</th>
                                <th className="text-center pb-2 font-medium">Raw Marks</th>
                                <th className="text-center pb-2 font-medium">CIE Weight</th>
                                <th className="text-right pb-2 font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                              {comp.children.map(child => {
                                const childContribution = child.contributionToCIE || 0;
                                const isSelected = child.isSelected === true;
                                
                                return (
                                  <tr key={child.id} className="text-slate-600 hover:bg-slate-50/50">
                                    <td className="py-2.5 font-medium text-slate-700">{child.name}</td>
                                    <td className="text-center py-2.5 font-mono">{child.rawScore}/{child.maxRawScore}</td>
                                    <td className="text-center py-2.5 font-mono text-indigo-600 font-medium">
                                      {childContribution.toFixed(2)}
                                    </td>
                                    <td className="text-right py-2.5">
                                      {isSelected ? (
                                        <span className="inline-flex items-center gap-1 text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded text-[10px] font-medium border border-emerald-100">
                                          Used
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded text-[10px] border border-slate-100">
                                          Omitted
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot className="bg-slate-50/70 border-t border-slate-100 font-semibold text-slate-700">
                              <tr>
                                <td className="py-2 px-2">Aggregated Summary</td>
                                <td className="text-center py-2 font-mono">{comp.rawScore}/{comp.maxRawScore}</td>
                                <td className="text-center py-2 font-mono text-indigo-600">{comp.contributionToCIE?.toFixed(2)}</td>
                                <td className="text-right py-2 pr-2 text-[10px] font-normal text-slate-400">
                                  {comp.children.filter(c => c.isSelected).length}/{comp.children.length} Used
                                </td>
                               </tr>
                            </tfoot>
                           </table>
                        </div>
                      )}
                      
                      {comp.type === 'single' && (
                        <div className="grid grid-cols-3 gap-2 text-xs p-3 bg-slate-50 rounded-xl border border-slate-100">
                          <div>
                            <span className="block text-slate-400 mb-0.5">Raw Score</span>
                            <span className="font-semibold text-slate-700 font-mono">{comp.rawScore}/{comp.maxRawScore}</span>
                          </div>
                          <div>
                            <span className="block text-slate-400 mb-0.5">Weightage</span>
                            <span className="font-semibold text-slate-700">{comp.weightageInCIE}%</span>
                          </div>
                          <div>
                            <span className="block text-slate-400 mb-0.5 text-right">CIE Fraction</span>
                            <span className="block font-bold text-indigo-600 text-right font-mono">{comp.contributionToCIE?.toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                      
                      {comp.type === 'attendance' && (
                        <div className="grid grid-cols-3 gap-2 text-xs p-3 bg-slate-50 rounded-xl border border-slate-100">
                          <div>
                            <span className="block text-slate-400 mb-0.5">Attendance</span>
                            <span className={`font-bold font-mono ${comp.isEligible ? 'text-emerald-600' : 'text-rose-600'}`}>
                              {comp.attendancePercentage?.toFixed(1)}%
                            </span>
                          </div>
                          <div>
                            <span className="block text-slate-400 mb-0.5">Institutional Minimum</span>
                            <span className="font-semibold text-slate-700 font-mono">{comp.requiredAttendance}%</span>
                          </div>
                          <div>
                            <span className="block text-slate-400 mb-0.5 text-right">CIE Allocation</span>
                            <span className="block font-bold text-indigo-600 text-right font-mono">{comp.contributionToCIE?.toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {selectedStudent.calculationHistory && selectedStudent.calculationHistory.length > 0 && (
              <div className="pt-2">
                <details className="group border border-slate-100 rounded-xl bg-slate-50/50 overflow-hidden">
                  <summary className="list-none flex items-center justify-between p-3 cursor-pointer select-none text-xs font-semibold text-slate-500 hover:text-slate-700">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      Audited Calculation Log History
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 transform group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-3 pb-3 divide-y divide-slate-100 border-t border-slate-100 bg-white max-h-36 overflow-y-auto">
                    {selectedStudent.calculationHistory.slice().reverse().slice(0, 5).map((history, idx) => (
                      <div key={idx} className="text-[11px] text-slate-500 py-2 flex flex-col sm:flex-row justify-between sm:items-center gap-1">
                        <span className="text-slate-400 font-mono">{new Date(history.calculatedAt).toLocaleString()}</span>
                        <div className="flex gap-2 font-medium">
                          <span>CIE: <strong className="text-slate-700 font-mono">{history.newTotalCIE?.toFixed(2)}</strong></span>
                          <span className={history.isEligible ? 'text-emerald-600' : 'text-rose-600'}>
                            ({history.isEligible ? 'Eligible' : 'Ineligible'})
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 bg-white rounded-xl border border-slate-100 shadow-sm">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="mt-3 text-sm font-medium text-slate-500">Processing academic matrices...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center bg-white rounded-xl border border-rose-100 max-w-md mx-auto my-12 shadow-sm">
        <div className="p-3 bg-rose-50 rounded-full text-rose-500 mb-3">
          <AlertCircle className="w-6 h-6" />
        </div>
        <h3 className="text-sm font-semibold text-slate-800">Data Aggregation Failed</h3>
        <p className="text-xs text-rose-600 mt-1 mb-4 font-medium">{error}</p>
        <button 
          onClick={fetchAggregatedMarks} 
          className="w-full sm:w-auto px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold shadow-sm transition-colors"
        >
          Retry Calculation Fetch
        </button>
      </div>
    );
  }

  if (!aggregatedData) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-slate-100 text-center">
        <BookOpen className="w-10 h-10 text-slate-300 mb-3" />
        <p className="text-slate-500 text-sm mb-4">Continuous Internal Evaluation summary uncomputed.</p>
        <button 
          onClick={fetchAggregatedMarks} 
          className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg text-xs shadow-sm transition-all"
        >
          Synchronize Ledger Data
        </button>
      </div>
    );
  }

  const totalStudents = sortedStudents.length;
  const eligibleCount = sortedStudents.filter(s => s.isEligible).length;
  const avgCIE = (aggregatedData.summary?.averageCIE || 0).toFixed(1);
  const cieMax = aggregatedData.summary?.cieMaxMarks || aggregatedData.template?.ciePercentage || 40;
  const passingRequired = aggregatedData.students[0]?.academicEligibility?.required || 40;

  return (
    <div className="w-full flex flex-col bg-slate-50/50 rounded-xl overflow-hidden border border-slate-200">
      
      {/* 1. Header Control Panel */}
      <div className="p-4 bg-white border-b border-slate-200 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-slate-800 tracking-tight flex items-center gap-1.5">
              <BookOpen className="w-4 h-4 text-indigo-600" />
              {aggregatedData.subject?.name || 'Subject Marks Ledger'}
            </h2>
            <p className="text-[11px] text-slate-400 font-medium mt-0.5">Code: {aggregatedData.subject?.code || 'N/A'}</p>
          </div>
          <div className="flex gap-2 lg:hidden">
            <button 
              onClick={() => setShowMobileFilters(!showMobileFilters)} 
              className={`p-2 border rounded-lg transition-colors ${showMobileFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white border-slate-200 text-slate-600'}`}
            >
              <SlidersHorizontal className="w-4 h-4" />
            </button>
            <button onClick={fetchAggregatedMarks} className="p-2 border border-slate-200 bg-white rounded-lg text-slate-600 hover:bg-slate-50">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Search & Filtering Inputs */}
        <div className={`flex-col md:flex-row items-stretch md:items-center gap-2 lg:flex ${showMobileFilters ? 'flex' : 'hidden lg:flex'}`}>
          <div className="relative flex-1 md:w-56">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search Name or Identifier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-slate-50/50"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2 md:flex md:items-center">
            <select
              value={eligibilityFilter}
              onChange={(e) => setEligibilityFilter(e.target.value)}
              className="w-full px-2.5 py-1.5 border border-slate-200 bg-white rounded-lg text-xs font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              <option value="all">All Registrations</option>
              <option value="eligible">Eligible Only</option>
              <option value="ineligible">Ineligible Status</option>
            </select>

            <button
              onClick={exportToCSV}
              className="w-full px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors shadow-sm"
            >
              <Download className="w-3.5 h-3.5 text-slate-500" />
              <span>Export CSV</span>
            </button>
          </div>

          <button
            onClick={fetchAggregatedMarks}
            className="hidden lg:block p-2 bg-white border border-slate-200 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors shadow-sm"
            title="Force Re-sync"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* 2. Top-Level High Value Performance Analytics Cards */}
      <div className="p-4 bg-white border-b border-slate-200 grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-2.5">
          <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600 shrink-0">
            <Users className="w-4 h-4" />
          </div>
          <div>
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Total Enrolled</span>
            <span className="text-base font-bold text-slate-800 font-mono mt-0.5 block">{totalStudents} <span className="text-xs font-normal text-slate-400">Students</span></span>
          </div>
        </div>

        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-2.5">
          <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600 shrink-0">
            <CheckCircle className="w-4 h-4" />
          </div>
          <div>
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Term Clearance</span>
            <span className="text-base font-bold text-emerald-600 font-mono mt-0.5 block">{eligibleCount} <span className="text-xs font-normal text-slate-400">Cleared</span></span>
          </div>
        </div>

        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-2.5">
          <div className="p-2 bg-blue-50 rounded-lg text-blue-600 shrink-0">
            <TrendingUp className="w-4 h-4" />
          </div>
          <div>
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Class Average</span>
            <span className="text-base font-bold text-slate-800 font-mono mt-0.5 block">{avgCIE}<span className="text-xs font-normal text-slate-400">/{cieMax} M</span></span>
          </div>
        </div>

        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-2.5">
          <div className="p-2 bg-purple-50 rounded-lg text-purple-600 shrink-0">
            <PieChart className="w-4 h-4" />
          </div>
          <div className="overflow-hidden w-full">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Evaluation Matrix</span>
            <span className="text-xs font-semibold text-purple-700 mt-1 block truncate" title={aggregatedData.template?.name}>
              {aggregatedData.template?.name || 'Standard'}
            </span>
          </div>
        </div>

        {/* MANUAL AGGREGATION BUTTON */}
        <div className="p-2 bg-amber-50 rounded-xl border border-amber-200 flex items-center justify-between gap-2 col-span-2 md:col-span-1">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-amber-100 rounded-lg text-amber-700 shrink-0">
              <Zap className="w-3.5 h-3.5" />
            </div>
            <div className="hidden sm:block">
              <span className="block text-[9px] font-bold uppercase tracking-wider text-amber-600">Sync Status</span>
              <span className="text-[10px] font-medium text-amber-700">CIE Aggregation</span>
            </div>
          </div>
          <button
            onClick={() => triggerManualAggregation('all')}
            disabled={recalculating}
            className="px-2.5 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-[10px] font-bold flex items-center gap-1.5 transition-all shadow-sm whitespace-nowrap"
            title="Recalculate CIE aggregation for all students (useful after attendance updates)"
          >
            {recalculating && recalcTarget === 'all' ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Zap className="w-3 h-3" />
            )}
            {recalculating && recalcTarget === 'all' ? 'Recalculating...' : 'Recalc All CIE'}
          </button>
        </div>
      </div>

      {/* Recalculation Progress Indicator */}
      {recalcProgress && (
        <div className="mx-4 mt-2 mb-0 p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="flex items-center gap-2 text-[11px] text-emerald-700">
            <CheckCircle className="w-3.5 h-3.5" />
            <span>Recalculation completed: {recalcProgress.totalProcessed || 0} students processed</span>
            {recalcProgress.successful !== undefined && (
              <span className="font-medium">({recalcProgress.successful} success, {recalcProgress.failed || 0} failed)</span>
            )}
          </div>
        </div>
      )}

      {/* Institutional Rule Banner Info */}
      <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 font-medium">
        <span>🎯 <span className="text-slate-400">CIE Max Score:</span> <strong className="text-slate-700">{cieMax} Marks</strong></span>
        <span>⚖️ <span className="text-slate-400">Min Passing Benchmark:</span> <strong className="text-slate-700">{passingRequired}% Score</strong></span>
        <span className="text-amber-600">⚡ <span className="text-slate-400">Need to recalculate after attendance update?</span> Click the orange button above</span>
      </div>

      {/* 3. Main Data Core Area */}
      
      {/* Mobile/Tablet Card View Container */}
      <div className="block xl:hidden p-4 space-y-3 overflow-y-auto max-h-[600px] bg-slate-50/50">
        {sortedStudents.map((student) => {
          const requiredPercentage = student.academicEligibility?.required || 40;
          return (
            <div key={student.studentId} className="bg-white rounded-xl border border-slate-150 p-4 shadow-sm space-y-3 hover:border-slate-300 transition-all">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="text-xs font-bold text-slate-800">{student.studentName || '—'}</h4>
                  <p className="text-[10px] font-mono text-slate-400 mt-0.5">{student.studentUSN || '—'}</p>
                  {!student.hasAggregation && (
                    <span className="inline-block text-[9px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded font-medium border border-amber-100 mt-1">Pending Entry</span>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xs font-bold font-mono text-slate-800">
                    CIE: <span className={student.totalCIEPercentage >= requiredPercentage ? 'text-emerald-600' : 'text-rose-600'}>
                      {student.hasAggregation ? student.totalCIEScore?.toFixed(2) : '—'}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-400 font-mono mt-0.5">{student.totalCIEPercentage?.toFixed(1) || 0}%</p>
                </div>
              </div>

              {/* Collapsible/Mini-summary of main components inside mobile cards */}
              <div className="pt-2 border-t border-slate-50 grid grid-cols-2 gap-2 text-[11px]">
                {displayColumns.slice(0, 2).map(col => {
                  const { displayValue, isEligible, hasData } = getComponentDisplay(student, col);
                  return (
                    <div key={col.id} className="bg-slate-50 p-2 rounded-lg border border-slate-100">
                      <span className="text-slate-400 block text-[9px] font-medium truncate">{col.name}</span>
                      <span className={`font-mono font-bold ${hasData ? (isEligible ? 'text-slate-700' : 'text-rose-600') : 'text-slate-300'}`}>
                        {hasData ? displayValue : '—'}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                <div>
                  {student.hasAggregation && (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-tight ${student.isEligible ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'}`}>
                      {student.isEligible ? 'Eligible' : 'Ineligible'}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => triggerManualAggregation('student', student.studentId)}
                    disabled={recalculating}
                    className="flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded-lg transition-colors"
                    title="Recalculate CIE for this student"
                  >
                    {recalculating && recalcTarget === 'student' ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Zap className="w-3 h-3" />
                    )}
                    Recalc
                  </button>
                  <button
                    onClick={() => viewStudentDetails(student)}
                    className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-lg transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    Audit
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop Rich Spreadsheet Matrix View */}
      <div className="hidden xl:block flex-1 overflow-x-auto bg-white">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider">
              <th className="w-10 px-3 py-3 text-center sticky left-0 bg-slate-50 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]"></th>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-slate-100 transition-colors border-r border-slate-200 sticky left-10 bg-slate-50 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] min-w-[110px]"
                onClick={() => handleSort('studentUSN')}
              >
                <div className="flex items-center gap-1">
                  USN Identifier
                  {sortColumn === 'studentUSN' && <ArrowUpDown className="w-3 h-3 text-indigo-500" />}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-slate-100 transition-colors border-r border-slate-200 sticky left-[150px] bg-slate-50 z-20 shadow-[3px_0_6px_-2px_rgba(0,0,0,0.08)] min-w-[200px]"
                onClick={() => handleSort('studentName')}
              >
                <div className="flex items-center gap-1">
                  Student Name
                  {sortColumn === 'studentName' && <ArrowUpDown className="w-3 h-3 text-indigo-500" />}
                </div>
              </th>
              
              {displayColumns.map(col => (
                <th key={col.id} className="px-4 py-3 text-center border-r border-slate-150 min-w-[140px] bg-slate-50/70 normal-case">
                  <div className="font-bold text-slate-700 truncate">{col.name}</div>
                  <div className="text-[10px] text-slate-400 font-normal mt-0.5 flex items-center justify-center gap-1">
                    <span>Wt: {col.weight}%</span>
                    {col.type === 'parent' && col.aggregationRule && (
                      <span className="bg-slate-200/80 text-slate-600 px-1 rounded text-[9px] font-medium scale-90">
                        {getAggregationRuleDisplay(col.aggregationRule, col.nValue)}
                      </span>
                    )}
                  </div>
                  <div className="text-[9px] text-slate-400 font-mono font-normal mt-0.5">
                    Max: {col.maxContribution.toFixed(0)} M
                  </div>
                </th>
              ))}
              
              <th 
                className="px-4 py-3 text-center cursor-pointer hover:bg-slate-100 border-r border-slate-200 min-w-[110px]"
                onClick={() => handleSort('totalCIEScore')}
              >
                <div className="flex items-center justify-center gap-1">
                  Total CIE
                  {sortColumn === 'totalCIEScore' && <ArrowUpDown className="w-3 h-3 text-indigo-500" />}
                </div>
                <div className="text-[9px] text-slate-400 font-mono font-normal mt-0.5">Max {cieMax} Marks</div>
              </th>
              <th 
                className="px-4 py-3 text-center cursor-pointer hover:bg-slate-100 border-r border-slate-200 min-w-[110px]"
                onClick={() => handleSort('eligibility')}
              >
                <div className="flex items-center justify-center gap-1">
                  Status
                  {sortColumn === 'eligibility' && <ArrowUpDown className="w-3 h-3 text-indigo-500" />}
                </div>
              </th>
              <th className="px-4 py-3 text-center min-w-[100px]">Actions</th>
             </tr>
          </thead>
          
          <tbody className="divide-y divide-slate-150 text-slate-600">
            {sortedStudents.map((student, idx) => {
              const requiredPercentage = student.academicEligibility?.required || 40;
              const isExpanded = expandedRows.has(student.studentId);
              
              return (
                <React.Fragment key={student.studentId}>
                  <tr className={`hover:bg-slate-50/80 transition-colors group ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/20'}`}>
                    {/* Expand Control Icon Button */}
                    <td className="px-2 py-3 text-center sticky left-0 bg-inherit z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]">
                      <button
                        onClick={() => toggleRowExpand(student.studentId)}
                        className="p-1 hover:bg-slate-200 text-slate-400 hover:text-slate-600 rounded transition-colors"
                        disabled={!student.hasAggregation}
                      >
                        {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </button>
                     </td>
                    
                    {/* Fixed Primary Row Identifiers */}
                    <td className="px-4 py-3 border-r border-slate-200 font-mono text-slate-700 font-medium sticky left-10 bg-inherit z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]">
                      {student.studentUSN || '—'}
                     </td>
                    <td className="px-4 py-3 border-r border-slate-200 font-semibold text-slate-800 sticky left-[150px] bg-inherit z-10 shadow-[3px_0_6px_-2px_rgba(0,0,0,0.08)]">
                      <span className="truncate block max-w-[180px]">{student.studentName || '—'}</span>
                      {!student.hasAggregation && (
                        <span className="text-[9px] font-medium text-amber-600 bg-amber-50 px-1 rounded border border-amber-100 mt-0.5 inline-block">Unentered Roll</span>
                      )}
                     </td>
                    
                    {/* Dynamic Structural Column Marks Mapping */}
                    {displayColumns.map(col => {
                      const { displayValue, percentage, hasData, isEligible } = getComponentDisplay(student, col);
                      
                      if (!hasData) {
                        return (
                          <td key={col.id} className="px-4 py-3 text-center border-r border-slate-150 text-slate-300">
                            <MinusCircle className="w-3.5 h-3.5 inline opacity-40" />
                           </td>
                        );
                      }
                      
                      return (
                        <td key={col.id} className="px-4 py-3 text-center border-r border-slate-150 group-hover:bg-slate-50/40">
                          <div className="inline-flex flex-col items-center">
                            <div className="flex items-center gap-1">
                              <span className={`font-mono font-bold ${!isEligible ? 'text-rose-600' : 'text-slate-700'}`}>
                                {displayValue}
                              </span>
                              {!isEligible ? (
                                <XCircle className="w-3 h-3 text-rose-400 shrink-0" />
                              ) : (
                                <CheckCircle className="w-3 h-3 text-emerald-400 shrink-0" />
                              )}
                            </div>
                            <span className="text-[10px] text-slate-400 font-mono mt-0.5">{percentage.toFixed(1)}%</span>
                          </div>
                         </td>
                      );
                    })}
                    
                    {/* Calculated Matrix Outputs */}
                    <td className="px-4 py-3 text-center border-r border-slate-200 font-mono bg-slate-50/20">
                      <span className={`font-bold text-sm ${student.totalCIEPercentage >= requiredPercentage ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {student.hasAggregation ? (student.totalCIEScore?.toFixed(2)) : '—'}
                      </span>
                      {student.hasAggregation && (
                        <div className="text-[10px] text-slate-400 font-medium">
                          {student.totalCIEPercentage?.toFixed(1)}%
                        </div>
                      )}
                     </td>
                    
                    <td className="px-4 py-3 text-center border-r border-slate-200">
                      {!student.hasAggregation ? (
                        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-400">Void</span>
                      ) : student.isEligible ? (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">Eligible</span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-100">Ineligible</span>
                      )}
                     </td>
                    
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => triggerManualAggregation('student', student.studentId)}
                          disabled={recalculating}
                          className="p-1.5 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Recalculate CIE for this student"
                        >
                          {recalculating && recalcTarget === 'student' ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Zap className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <button
                          onClick={() => viewStudentDetails(student)}
                          className="p-1.5 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="View Full Matrix Audit"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                     </td>
                   </tr>
                  
                  {/* Expanded Multi-assessment Dynamic Child Rows */}
                  {isExpanded && student.hasAggregation && (
                    <tr className="bg-slate-50/50">
                      <td colSpan={displayColumns.length + 7} className="px-6 py-4 border-b border-slate-200">
                        <div className="text-xs">
                          <div className="font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            Internal Ledger Component Breakdowns
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {student.components?.filter(comp => comp.type === 'parent' && comp.children?.length > 0).map(comp => {
                              const column = displayColumns.find(c => c.id === comp.id);
                              const aggregationRule = column?.aggregationRule || comp.aggregationRuleApplied;
                              const nValue = column?.nValue || comp.nValue;
                              const maxContribution = column?.maxContribution || getComponentMaxContribution(comp.weightageInCIE);
                              const earnedScore = comp.contributionToCIE || 0;
                              const ruleDisplay = getAggregationRuleDisplay(aggregationRule, nValue);
                              
                              return (
                                <div key={comp.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                                  <div className="bg-slate-50 px-3.5 py-2 font-semibold border-b border-slate-150 flex justify-between items-center text-slate-700">
                                    <span>{comp.name}</span>
                                    <span className="text-[11px] text-slate-400 font-normal">Weight: {comp.weightageInCIE}%</span>
                                  </div>
                                  <div className="p-3">
                                    <div className="text-[11px] text-slate-400 flex justify-between pb-2 mb-2 border-b border-dashed border-slate-100">
                                      <span>Rule Strategy: <strong className="text-indigo-600 font-medium">{ruleDisplay || 'Standard'}</strong></span>
                                      <span className="font-mono">Contribution: <strong className="text-slate-700">{earnedScore.toFixed(2)}/{maxContribution.toFixed(0)}</strong></span>
                                    </div>
                                    
                                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                      {comp.children.map(child => (
                                        <div key={child.id} className="flex justify-between items-center text-[11px] py-1 hover:bg-slate-50/50 px-1 rounded">
                                          <span className="font-medium text-slate-600">{child.name}</span>
                                          <div className="flex items-center gap-3 font-mono">
                                            <span className="text-slate-400">{child.rawScore}/{child.maxRawScore}</span>
                                            <span className="font-bold text-indigo-600 w-10 text-right">
                                              {child.contributionToCIE?.toFixed(2)}
                                            </span>
                                            {child.isSelected ? (
                                              <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" title="Selected" />
                                            ) : (
                                              <XCircle className="w-3.5 h-3.5 text-slate-300 shrink-0" title="Omitted" />
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                       </td>
                     </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
         </table>
      </div>

      {sortedStudents.length === 0 && (
        <div className="text-center py-16 bg-white text-slate-400 text-xs font-medium border-t border-slate-200">
          No student rosters match the selected data filter or search strings.
        </div>
      )}
      
      {showDetailsModal && <StudentDetailsModal />}
    </div>
  );
};

export default AggregatedMarksView;